import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
    BootstrapSetupApplyResponse,
    BootstrapSetupConnectionRequest,
    BootstrapPublicAuthConfig,
    BootstrapSetupStatus,
    DatabaseMigrationJobStatus,
    DatabaseMigrationPreflightRequest,
    DatabaseMigrationPreflightResult,
    DatabaseMigrationSourceDefault,
    DatabaseMigrationStartRequest,
    InitialSetupStatus,
    InitialSetupValues,
    SetupEmailTestRequest
} from './initial-setup.model';
import { GlobalVariablesService } from '@app/core/services/global-variables.service';

@Injectable({
    providedIn: 'root'
})
export class InitialSetupService {

    private readonly databaseMigrationWatchKey = 'navizard.databaseMigration.watch';
    private statusSubject = new ReplaySubject<InitialSetupStatus>(1);
    private currentStatus?: InitialSetupStatus;
    private inflight?: Promise<InitialSetupStatus>;
    private currentBootstrapStatus?: BootstrapSetupStatus;
    private bootstrapInflight?: Promise<BootstrapSetupStatus>;
    private currentDatabaseMigrationStatus?: DatabaseMigrationJobStatus;
    private databaseMigrationStatusInflight?: Promise<DatabaseMigrationJobStatus>;
    private databaseMigrationStatusCheckedAt = 0;

    constructor(
        private http: HttpClient,
        private globals: GlobalVariablesService
    ) { }

    get status$() {
        return this.statusSubject.asObservable();
    }

    async fetchBootstrapStatus(force = false): Promise<BootstrapSetupStatus> {
        if (!force && this.currentBootstrapStatus && this.isBootstrapSetupComplete(this.currentBootstrapStatus)) {
            return this.currentBootstrapStatus;
        }

        if (!force && this.bootstrapInflight) {
            return this.bootstrapInflight;
        }

        const request = firstValueFrom(
            this.http
                .get(`${environment.apiv2}/bootstrap-setup/status`, {
                    headers: this.buildHeaders(),
                    responseType: 'text'
                })
                .pipe(map((body: string) => this.parseBootstrapStatus(body)))
        )
            .then((status) => {
                if (this.bootstrapInflight === request) {
                    this.currentBootstrapStatus = status;
                }
                return status;
            })
            .finally(() => {
                if (this.bootstrapInflight !== request) {
                    return;
                }
                this.bootstrapInflight = undefined;
            });

        this.bootstrapInflight = request;
        return request;
    }

    async fetchStatus(force = false): Promise<InitialSetupStatus> {
        void force;

        if (!this.inflight) {
            this.inflight = firstValueFrom(
                this.http
                    .get(`${environment.apiv2}/setup/status`, {
                        headers: this.buildHeaders(),
                        responseType: 'text'
                    })
                    .pipe(map((body: string) => this.parseStatus(body)))
            )
                .then((status) => this.persistStatus(status))
                .catch((error) => {
                    if (this.isSetupLockedResponse(error)) {
                        return this.persistStatus(this.createLockedStatus());
                    }
                    throw error;
                })
                .finally(() => {
                    this.inflight = undefined;
                });
        }

        return this.inflight;
    }

    async testBootstrapConnection(payload: BootstrapSetupConnectionRequest): Promise<void> {
        await firstValueFrom(
            this.http.post(`${environment.apiv2}/bootstrap-setup/test-connection`, payload, {
                headers: this.buildHeaders()
            })
        );
    }

    async applyBootstrapSetup(payload: BootstrapSetupConnectionRequest): Promise<BootstrapSetupApplyResponse> {
        const result = await firstValueFrom(
            this.http.post<BootstrapSetupApplyResponse>(`${environment.apiv2}/bootstrap-setup/apply`, payload, {
                headers: this.buildHeaders()
            })
        );
        this.clearBootstrapStatusCache();
        return result;
    }

    async fetchPublicAuthConfig(): Promise<BootstrapPublicAuthConfig> {
        const payload = await firstValueFrom(
            this.http.get<BootstrapPublicAuthConfig>(`${environment.apiv2}/bootstrap-setup/public-auth`, {
                headers: this.buildHeaders()
            })
        );

        return {
            provider: this.coalesce(payload?.provider, 'Local'),
            projectUrl: this.coalesce(payload?.projectUrl, ''),
            anonKey: this.coalesce(payload?.anonKey, ''),
            authBaseUrl: this.coalesce(payload?.authBaseUrl, '')
        };
    }

    async completeSetup(payload: InitialSetupValues): Promise<InitialSetupStatus> {
        const status = await firstValueFrom(
            this.http.post<InitialSetupStatus>(`${environment.apiv2}/setup`, payload, {
                headers: this.buildHeaders()
            })
        );
        this.clearBootstrapStatusCache();
        return this.persistStatus(status);
    }

    async fetchReviewStatus(force = false): Promise<InitialSetupStatus> {
        void force;

        const status = await firstValueFrom(
            this.http
                .get(`${environment.apiv2}/setup/review-status`, {
                    headers: this.buildHeaders(),
                    responseType: 'text'
                })
                .pipe(map((body: string) => this.parseStatus(body)))
        );
        return this.persistStatus(status);
    }

    async completeSetupReview(payload: InitialSetupValues): Promise<InitialSetupStatus> {
        const status = await firstValueFrom(
            this.http.post<InitialSetupStatus>(`${environment.apiv2}/setup/review`, payload, {
                headers: this.buildHeaders()
            })
        );
        this.clearBootstrapStatusCache();
        return this.persistStatus(this.normalizeStatus(status));
    }

    async testEmailSettings(payload: SetupEmailTestRequest): Promise<void> {
        await firstValueFrom(
            this.http.post(`${environment.apiv2}/setup/test-email`, payload, {
                headers: this.buildHeaders()
            })
        );
    }

    async fetchDatabaseMigrationStatus(force = false): Promise<DatabaseMigrationJobStatus> {
        const now = Date.now();
        if (!force && this.currentDatabaseMigrationStatus && now - this.databaseMigrationStatusCheckedAt < 10000) {
            return this.currentDatabaseMigrationStatus;
        }

        if (!this.databaseMigrationStatusInflight) {
            this.databaseMigrationStatusInflight = firstValueFrom(
                this.http.get<any>(`${environment.apiv2}/database-migrations/status`, {
                    headers: this.buildHeaders()
                })
            )
                .then((payload) => this.persistDatabaseMigrationWatch(this.normalizeMigrationStatus(payload)))
                .finally(() => {
                    this.databaseMigrationStatusInflight = undefined;
                });
        }

        this.currentDatabaseMigrationStatus = await this.databaseMigrationStatusInflight;
        this.databaseMigrationStatusCheckedAt = Date.now();
        return this.currentDatabaseMigrationStatus;
    }

    async fetchDatabaseMigrationSourceDefault(): Promise<DatabaseMigrationSourceDefault> {
        const payload = await firstValueFrom(
            this.http.get<any>(`${environment.apiv2}/database-migrations/source-default`, {
                headers: this.buildHeaders()
            })
        );
        const values = payload?.values ?? payload?.Values ?? {};
        return {
            hasDefaultSource: this.coalesce(payload?.hasDefaultSource, payload?.HasDefaultSource, false),
            provider: this.coalesce(payload?.provider, payload?.Provider, 'SqlServer'),
            maskedConnectionString: this.coalesce(payload?.maskedConnectionString, payload?.MaskedConnectionString, ''),
            values: {
                useDefaultSource: this.coalesce(values.useDefaultSource, values.UseDefaultSource, false),
                connectionMode: this.coalesce(values.connectionMode, values.ConnectionMode, 'ConnectionString'),
                connectionString: this.coalesce(values.connectionString, values.ConnectionString, ''),
                host: this.coalesce(values.host, values.Host, ''),
                port: this.coalesce(values.port, values.Port, null),
                database: this.coalesce(values.database, values.Database, ''),
                username: this.coalesce(values.username, values.Username, ''),
                password: this.coalesce(values.password, values.Password, ''),
                trustServerCertificate: this.coalesce(values.trustServerCertificate, values.TrustServerCertificate, true),
                multipleActiveResultSets: this.coalesce(values.multipleActiveResultSets, values.MultipleActiveResultSets, true)
            }
        };
    }

    async preflightDatabaseMigration(payload: DatabaseMigrationPreflightRequest): Promise<DatabaseMigrationPreflightResult> {
        const result = await firstValueFrom(
            this.http.post<any>(`${environment.apiv2}/database-migrations/preflight`, payload, {
                headers: this.buildHeaders()
            })
        );
        return this.normalizeMigrationPreflight(result);
    }

    async startDatabaseMigration(payload: DatabaseMigrationStartRequest): Promise<DatabaseMigrationJobStatus> {
        const result = await firstValueFrom(
            this.http.post<any>(`${environment.apiv2}/database-migrations/jobs`, payload, {
                headers: this.buildHeaders()
            })
        );
        return this.persistDatabaseMigrationWatch(this.normalizeMigrationStatus(result));
    }

    async cancelDatabaseMigration(jobId: string): Promise<DatabaseMigrationJobStatus> {
        const result = await firstValueFrom(
            this.http.post<any>(`${environment.apiv2}/database-migrations/jobs/${jobId}/cancel`, {}, {
                headers: this.buildHeaders()
            })
        );
        return this.persistDatabaseMigrationWatch(this.normalizeMigrationStatus(result));
    }

    shouldCheckDatabaseMigrationStatus(): boolean {
        if (typeof window === 'undefined' || !window.localStorage) {
            return false;
        }

        return window.localStorage.getItem(this.databaseMigrationWatchKey) === 'true';
    }

    markDatabaseMigrationStatusWatch(): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        window.localStorage.setItem(this.databaseMigrationWatchKey, 'true');
    }

    private parseStatus(raw: string): InitialSetupStatus {
        if (!raw) {
            return this.createDefaultStatus();
        }

        try {
            const parsed = JSON.parse(raw);
            return this.normalizeStatus(parsed);
        } catch (error) {
            console.error('Failed to parse setup status payload', error, raw);
            return this.createDefaultStatus();
        }
    }

    private parseBootstrapStatus(raw: string): BootstrapSetupStatus {
        if (!raw) {
            return this.createUnavailableBootstrapStatus('Bootstrap setup status response was empty.');
        }

        try {
            const parsed = JSON.parse(raw);
            return this.normalizeBootstrapStatus(parsed);
        } catch (error) {
            console.error('Failed to parse bootstrap setup status payload', error, raw);
            return this.createUnavailableBootstrapStatus('Bootstrap setup status response could not be parsed.');
        }
    }

    private normalizeStatus(payload: any): InitialSetupStatus {
        if (!payload) {
            return this.createDefaultStatus();
        }

        const values = payload.values ?? payload.Values ?? {};

        return {
            isConfigured: this.coalesce(payload.isConfigured, payload.IsConfigured, false),
            hasSmtpPassword: this.coalesce(payload.hasSmtpPassword, payload.HasSmtpPassword, false),
            usesLegacyConfiguration: this.coalesce(payload.usesLegacyConfiguration, payload.UsesLegacyConfiguration, false),
            usesSupabaseAuth: this.coalesce(payload.usesSupabaseAuth, payload.UsesSupabaseAuth, false),
            requiresAdminSetupReview: this.coalesce(payload.requiresAdminSetupReview, payload.RequiresAdminSetupReview, false),
            hasApplicationUsers: this.coalesce(payload.hasApplicationUsers, payload.HasApplicationUsers, false),
            setupMode: this.coalesce(payload.setupMode, payload.SetupMode, 'PublicApplicationSetup'),
            missingFields: this.coalesce(payload.missingFields, payload.MissingFields, []),
            values: {
                customerName: this.coalesce(values.customerName, values.CustomerName, ''),
                websiteAddress: this.coalesce(values.websiteAddress, values.WebsiteAddress, ''),
                photoServerAddress: this.coalesce(values.photoServerAddress, values.PhotoServerAddress, ''),
                trackerSignalRAddress: this.coalesce(values.trackerSignalRAddress, values.TrackerSignalRAddress, ''),
                timeZone: this.coalesce(values.timeZone, values.TimeZone, 'Europe/Istanbul'),
                emailServer: this.coalesce(values.emailServer, values.EmailServer, ''),
                emailPort: this.coalesce(values.emailPort, values.EmailPort, null),
                enableSsl: this.coalesce(values.enableSsl, values.EnableSsl, true),
                emailUsername: this.coalesce(values.emailUsername, values.EmailUsername, ''),
                emailPassword: this.coalesce(values.emailPassword, values.EmailPassword, ''),
                emailSender: this.coalesce(values.emailSender, values.EmailSender, ''),
                replyTo: this.coalesce(values.replyTo, values.ReplyTo, ''),
                adminEmail: this.coalesce(values.adminEmail, values.AdminEmail, ''),
                adminPassword: this.coalesce(values.adminPassword, values.AdminPassword, ''),
                adminDisplayName: this.coalesce(values.adminDisplayName, values.AdminDisplayName, '')
            }
        };
    }

    private normalizeBootstrapStatus(payload: any): BootstrapSetupStatus {
        if (!payload) {
            return this.createDefaultBootstrapStatus();
        }

        const values = payload.values ?? payload.Values ?? {};
        const bootstrapState = this.coalesce(payload.bootstrapState, payload.BootstrapState, 'Unconfigured');
        const databaseConfigured = this.coalesce(
            payload.databaseConfigured,
            payload.DatabaseConfigured,
            bootstrapState !== 'Unconfigured'
        );
        const databaseReachable = this.coalesce(
            payload.databaseReachable,
            payload.DatabaseReachable,
            bootstrapState === 'Ready'
        );
        const canEditDatabaseConfiguration = this.coalesce(
            payload.canEditDatabaseConfiguration,
            payload.CanEditDatabaseConfiguration,
            !databaseConfigured && bootstrapState === 'Unconfigured'
        );

        return {
            bootstrapState,
            bootstrapReady: this.coalesce(payload.bootstrapReady, payload.BootstrapReady, false),
            applicationSetupReady: this.coalesce(payload.applicationSetupReady, payload.ApplicationSetupReady, false),
            canAttemptConnection: this.coalesce(payload.canAttemptConnection, payload.CanAttemptConnection, canEditDatabaseConfiguration),
            databaseConfigured,
            databaseReachable,
            canEditDatabaseConfiguration,
            configurationSource: this.coalesce(payload.configurationSource, payload.ConfigurationSource, databaseConfigured ? 'Unknown' : 'None'),
            connectionDetailsRedacted: this.coalesce(payload.connectionDetailsRedacted, payload.ConnectionDetailsRedacted, databaseConfigured),
            usesBootstrapConfiguration: this.coalesce(payload.usesBootstrapConfiguration, payload.UsesBootstrapConfiguration, false),
            usesLegacyConfiguration: this.coalesce(payload.usesLegacyConfiguration, payload.UsesLegacyConfiguration, false),
            requiresAdminSetupReview: this.coalesce(payload.requiresAdminSetupReview, payload.RequiresAdminSetupReview, false),
            hasApplicationUsers: this.coalesce(payload.hasApplicationUsers, payload.HasApplicationUsers, false),
            setupMode: this.coalesce(payload.setupMode, payload.SetupMode, 'DatabaseSetup'),
            restartPending: this.coalesce(payload.restartPending, payload.RestartPending, false),
            message: this.coalesce(payload.message, payload.Message, ''),
            values: {
                selectedProvider: this.coalesce(values.selectedProvider, values.SelectedProvider, 'SqlServer'),
                connectionMode: this.coalesce(values.connectionMode, values.ConnectionMode, 'ConnectionString'),
                connectionString: this.coalesce(values.connectionString, values.ConnectionString, ''),
                host: this.coalesce(values.host, values.Host, ''),
                port: this.coalesce(values.port, values.Port, null),
                database: this.coalesce(values.database, values.Database, ''),
                username: this.coalesce(values.username, values.Username, ''),
                password: this.coalesce(values.password, values.Password, ''),
                sslMode: this.coalesce(values.sslMode, values.SslMode, ''),
                trustServerCertificate: this.coalesce(values.trustServerCertificate, values.TrustServerCertificate, true),
                multipleActiveResultSets: this.coalesce(values.multipleActiveResultSets, values.MultipleActiveResultSets, true),
                initializeSchema: this.coalesce(values.initializeSchema, values.InitializeSchema, true),
                supabaseProjectUrl: this.coalesce(values.supabaseProjectUrl, values.SupabaseProjectUrl, ''),
                supabaseAnonKey: this.coalesce(values.supabaseAnonKey, values.SupabaseAnonKey, ''),
                supabaseServiceRoleKey: this.coalesce(values.supabaseServiceRoleKey, values.SupabaseServiceRoleKey, ''),
                supabaseJwksUrl: this.coalesce(values.supabaseJwksUrl, values.SupabaseJwksUrl, ''),
                hasSupabaseServiceRoleKey: this.coalesce(values.hasSupabaseServiceRoleKey, values.HasSupabaseServiceRoleKey, false)
            }
        };
    }

    private normalizeMigrationPreflight(payload: any): DatabaseMigrationPreflightResult {
        const tables = this.coalesce(payload?.tables, payload?.Tables, []);
        return {
            canStart: this.coalesce(payload?.canStart, payload?.CanStart, false),
            sourceConnectionOk: this.coalesce(payload?.sourceConnectionOk, payload?.SourceConnectionOk, false),
            targetConnectionOk: this.coalesce(payload?.targetConnectionOk, payload?.TargetConnectionOk, false),
            targetIsPostgreSql: this.coalesce(payload?.targetIsPostgreSql, payload?.TargetIsPostgreSql, false),
            targetHasData: this.coalesce(payload?.targetHasData, payload?.TargetHasData, false),
            requiresTargetClearConfirmation: this.coalesce(payload?.requiresTargetClearConfirmation, payload?.RequiresTargetClearConfirmation, false),
            includeHistory: this.coalesce(payload?.includeHistory, payload?.IncludeHistory, true),
            tableCount: this.coalesce(payload?.tableCount, payload?.TableCount, 0),
            totalRows: this.coalesce(payload?.totalRows, payload?.TotalRows, 0),
            historyRows: this.coalesce(payload?.historyRows, payload?.HistoryRows, 0),
            message: this.coalesce(payload?.message, payload?.Message, ''),
            warnings: this.coalesce(payload?.warnings, payload?.Warnings, []),
            errors: this.coalesce(payload?.errors, payload?.Errors, []),
            tables: tables.map((table: any) => this.normalizeMigrationTable(table))
        };
    }

    private normalizeMigrationStatus(payload: any): DatabaseMigrationJobStatus {
        const tables = this.coalesce(payload?.tables, payload?.Tables, []);
        return {
            id: this.coalesce(payload?.id, payload?.Id, undefined),
            state: this.coalesce(payload?.state, payload?.State, 'Idle'),
            hasActiveJob: this.coalesce(payload?.hasActiveJob, payload?.HasActiveJob, false),
            includeHistory: this.coalesce(payload?.includeHistory, payload?.IncludeHistory, false),
            clearTargetBeforeCopy: this.coalesce(payload?.clearTargetBeforeCopy, payload?.ClearTargetBeforeCopy, false),
            currentTable: this.coalesce(payload?.currentTable, payload?.CurrentTable, ''),
            totalTables: this.coalesce(payload?.totalTables, payload?.TotalTables, 0),
            processedTables: this.coalesce(payload?.processedTables, payload?.ProcessedTables, 0),
            totalRows: this.coalesce(payload?.totalRows, payload?.TotalRows, 0),
            copiedRows: this.coalesce(payload?.copiedRows, payload?.CopiedRows, 0),
            verifiedRows: this.coalesce(payload?.verifiedRows, payload?.VerifiedRows, 0),
            progressPercent: Number(this.coalesce(payload?.progressPercent, payload?.ProgressPercent, 0)),
            estimatedSecondsRemaining: this.coalesce(payload?.estimatedSecondsRemaining, payload?.EstimatedSecondsRemaining, null),
            startedAtUtc: this.coalesce(payload?.startedAtUtc, payload?.StartedAtUtc, undefined),
            completedAtUtc: this.coalesce(payload?.completedAtUtc, payload?.CompletedAtUtc, undefined),
            failedAtUtc: this.coalesce(payload?.failedAtUtc, payload?.FailedAtUtc, undefined),
            lastHeartbeatAtUtc: this.coalesce(payload?.lastHeartbeatAtUtc, payload?.LastHeartbeatAtUtc, undefined),
            errorMessage: this.coalesce(payload?.errorMessage, payload?.ErrorMessage, ''),
            maskedSource: this.coalesce(payload?.maskedSource, payload?.MaskedSource, ''),
            tables: tables.map((table: any) => this.normalizeMigrationTable(table))
        };
    }

    private persistDatabaseMigrationWatch(status: DatabaseMigrationJobStatus): DatabaseMigrationJobStatus {
        if (status.hasActiveJob) {
            this.markDatabaseMigrationStatusWatch();
            return status;
        }

        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem(this.databaseMigrationWatchKey);
        }

        return status;
    }

    private clearBootstrapStatusCache(): void {
        this.currentBootstrapStatus = undefined;
        this.bootstrapInflight = undefined;
    }

    private isBootstrapSetupComplete(status: BootstrapSetupStatus): boolean {
        const setupMode = (status.setupMode ?? '').toLowerCase();
        const bootstrapState = (status.bootstrapState ?? '').toLowerCase();

        return status.bootstrapReady
            && status.applicationSetupReady
            && (setupMode === 'ready' || bootstrapState === 'ready')
            && !status.requiresAdminSetupReview
            && !status.restartPending;
    }

    private normalizeMigrationTable(table: any) {
        return {
            id: this.coalesce(table?.id, table?.Id, undefined),
            tableName: this.coalesce(table?.tableName, table?.TableName, ''),
            entityName: this.coalesce(table?.entityName, table?.EntityName, ''),
            state: this.coalesce(table?.state, table?.State, 'Pending'),
            isHistoryTable: this.coalesce(table?.isHistoryTable, table?.IsHistoryTable, false),
            sortOrder: this.coalesce(table?.sortOrder, table?.SortOrder, 0),
            totalRows: this.coalesce(table?.totalRows, table?.TotalRows, 0),
            copiedRows: this.coalesce(table?.copiedRows, table?.CopiedRows, 0),
            verifiedRows: this.coalesce(table?.verifiedRows, table?.VerifiedRows, 0),
            startedAtUtc: this.coalesce(table?.startedAtUtc, table?.StartedAtUtc, undefined),
            completedAtUtc: this.coalesce(table?.completedAtUtc, table?.CompletedAtUtc, undefined),
            errorMessage: this.coalesce(table?.errorMessage, table?.ErrorMessage, '')
        };
    }

    private coalesce<T>(...values: Array<T | undefined>): T {
        for (const value of values) {
            if (value !== undefined && value !== null) {
                return value;
            }
        }
        return values[values.length - 1] as T;
    }

    private createDefaultStatus(): InitialSetupStatus {
        return {
            isConfigured: false,
            hasSmtpPassword: false,
            usesLegacyConfiguration: false,
            usesSupabaseAuth: false,
            requiresAdminSetupReview: false,
            hasApplicationUsers: false,
            setupMode: 'PublicApplicationSetup',
            missingFields: [],
            values: {
                customerName: '',
                websiteAddress: '',
                photoServerAddress: '',
                trackerSignalRAddress: '',
                timeZone: 'Europe/Istanbul',
                emailServer: '',
                emailPort: null,
                enableSsl: true,
                emailUsername: '',
                emailPassword: '',
                emailSender: '',
                replyTo: '',
                adminEmail: '',
                adminPassword: '',
                adminDisplayName: ''
            }
        };
    }

    private createDefaultBootstrapStatus(): BootstrapSetupStatus {
        return {
            bootstrapState: 'Unconfigured',
            bootstrapReady: false,
            applicationSetupReady: false,
            canAttemptConnection: true,
            databaseConfigured: false,
            databaseReachable: false,
            canEditDatabaseConfiguration: true,
            configurationSource: 'None',
            connectionDetailsRedacted: false,
            usesBootstrapConfiguration: false,
            usesLegacyConfiguration: false,
            requiresAdminSetupReview: false,
            hasApplicationUsers: false,
            setupMode: 'DatabaseSetup',
            restartPending: false,
            message: '',
            values: {
                selectedProvider: 'SqlServer',
                connectionMode: 'ConnectionString',
                connectionString: '',
                host: '',
                port: null,
                database: '',
                username: '',
                password: '',
                sslMode: '',
                trustServerCertificate: true,
                multipleActiveResultSets: true,
                initializeSchema: true,
                supabaseProjectUrl: '',
                supabaseAnonKey: '',
                supabaseServiceRoleKey: '',
                supabaseJwksUrl: '',
                hasSupabaseServiceRoleKey: false
            }
        };
    }

    private createUnavailableBootstrapStatus(message: string): BootstrapSetupStatus {
        const status = this.createDefaultBootstrapStatus();
        return {
            ...status,
            bootstrapState: 'ConfiguredButUnavailable',
            canAttemptConnection: false,
            databaseConfigured: true,
            databaseReachable: false,
            canEditDatabaseConfiguration: false,
            configurationSource: 'Unknown',
            connectionDetailsRedacted: true,
            setupMode: 'DatabaseUnavailable',
            message
        };
    }

    private createLockedStatus(): InitialSetupStatus {
        const base = this.createDefaultStatus();
        return {
            ...base,
            isConfigured: true
        };
    }

    private isSetupLockedResponse(error: any): boolean {
        return error instanceof HttpErrorResponse && error.status === 403;
    }

    private buildHeaders(): HttpHeaders | undefined {
        const tenantId = this.globals.app?.tenantId;
        if (tenantId) {
            return new HttpHeaders({
                TenantId: tenantId,
                Accept: 'application/json'
            });
        }
        return undefined;
    }

    private persistStatus(status: InitialSetupStatus): InitialSetupStatus {
        this.currentStatus = status;
        this.statusSubject.next(status);
        return status;
    }
}
